'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ArrowSelectIcon } from 'lib/svgs/Icons';
import * as React from 'react';
import { cn } from 'utils/cn';
import Typography from './Typography';

function AccordionUI({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        'border-b border-tertiary-600 outline-primary-blue-100 last:border-b-0',
        className
      )}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  notCollapsible,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & { notCollapsible?: boolean }) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'AccordionTrigger',
          'px-4 text-tertiary-100',
          'focus-visible:border-ring flex flex-1 items-start justify-between gap-4 rounded-md py-2',
          'text-left outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-primary-blue-300/50',
          'disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
          className
        )}
        {...props}
      >
        <Typography variant="medium2">{children}</Typography>
        {!notCollapsible && (
          <ArrowSelectIcon
            alt="Arrow Icon"
            height={20}
            width={20}
            className="AccordionContent pointer-events-none shrink-0 translate-y-1 fill-tertiary-200 transition-transform duration-200"
          />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="AccordionContent data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      {...props}
    >
      <div className={cn('pb-4 pt-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { AccordionContent, AccordionItem, AccordionTrigger, AccordionUI };
